import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import styles from "./workDayInfo.module.less"
import {WorkdayWithEmployee} from "@src/httpClient/__generated__/Api.ts";
import {WorkDayWithEvents} from "@src/Store/employeesStore.ts";
import React, {useMemo} from "react";
import {formatMsToJpTime, getTimeDiffMs, strToTime} from "@src/utils/formatDate.ts";
import {CheckInOutPair} from "@src/httpClient/__generated__/Api.ts";
import {
    Body_14_Coral_Primary_Regular_14,
    Body_16_Coral_Strong_emphasis_16
} from "@UI/typography/generated/typography.tsx";
import {Gt} from "@src/i18n/Gt";
import {Sun} from "@UI/components/icons/Sun.tsx";
import cn from "classnames";
import {Checked} from "@UI/components/icons/Checked.tsx";
import {useNetworkWatcher} from "@src/utils/net.ts";
import {Colors} from "@UI/theme/generated/Colors.ts";
import {EmployeeStatus, workdayStatus} from "@src/CommonComponents/WorkStatus/WorkStatus.tsx";
import {calculateWorkingTime} from "@src/utils/workingTimeCalculators.ts";
import {WorkDay} from "@src/db.ts";


const OnlineView = ({workDay, events}: {
    workDay?: WorkdayWithEmployee | WorkDayWithEvents | WorkDay,
    events: CheckInOutPair[],
}) => {
    const currentWorkDayLength = useMemo(() => {
        if (!workDay?.start_time || !workDay?.end_time) return 0
        return getTimeDiffMs(workDay.end_time, workDay.start_time)
    }, [workDay])

    const workedHoursMS = useMemo(() => {
        if (!events) return 0
        return calculateWorkingTime(events, workDay?.total_time ? strToTime(workDay.total_time) : undefined)
    }, [events, workDay])

    const workedHours = useMemo(() => {
        if (workedHoursMS === 0) return "-"
        return formatMsToJpTime(workedHoursMS)
    }, [workedHoursMS])

    return (
        <Stack direction={"column"} grow justifyContent={"center"} >
            <Body_14_Coral_Primary_Regular_14>
                <Gt>Working hours</Gt>
            </Body_14_Coral_Primary_Regular_14>
            <Stack grow justifyContent={"space-between"}>
                <Body_16_Coral_Strong_emphasis_16>
                    <span
                        className={cn(workedHoursMS < currentWorkDayLength ? styles.tooShort : [styles.normalLen])}>
                        {workedHours}
                    </span>
                </Body_16_Coral_Strong_emphasis_16>
                <Stack className={styles.info}>
                    <Checked/>
                    <Body_14_Coral_Primary_Regular_14>
                        <Gt>Including breaks</Gt>
                    </Body_14_Coral_Primary_Regular_14>
                </Stack>
            </Stack>
        </Stack>
    )
}


const OfflineView = () => {
    return (
         <Stack direction={"column"} grow justifyContent={"center"}>
            <Body_14_Coral_Primary_Regular_14>
                <Gt>Working hours</Gt>
            </Body_14_Coral_Primary_Regular_14>
             <Stack grow justifyContent={"space-between"}>
                 <Body_16_Coral_Strong_emphasis_16 style={{color: Colors.Coral_special_Error_red}}>
                     <Gt>Working hours cannot be calculated</Gt>
                 </Body_16_Coral_Strong_emphasis_16>
             </Stack>
        </Stack>
    )
}

export const WorkDayInfo = ({workDay, events, className, style, showGreetings = true}: {
    workDay?: WorkdayWithEmployee | WorkDayWithEvents | WorkDay,
    events: CheckInOutPair[],
    showGreetings?: boolean,
    style?: React.CSSProperties
    className?: string

}) => {
    const {online} = useNetworkWatcher()
    const workStatus = workdayStatus(workDay)
    
    return (
        <Stack className={cn(styles.root, className)} direction={"column"} style={style}>
            {showGreetings &&
                <Body_16_Coral_Strong_emphasis_16>
                    <Gt>Thank you for your hard work today! Today's summary is here</Gt>
                </Body_16_Coral_Strong_emphasis_16>
            }

            <Stack alignItems={"center"} gap={16}>
                <Sun />

                {(online && (workStatus !== EmployeeStatus.wrongData))
                    ? <OnlineView events={events} workDay={workDay} />
                    : <OfflineView />
                }
            </Stack>
        </Stack>
    )
}